// Managed funds (Vanguard) — finance table, per-user only (see store.js).
//
// Stored rows:
//   • fund#<productId>  — a fund held: `{ productId (APIR code, e.g.
//     VAN0111AU), name, portId, shortName }` (`shortName` null = derived
//     from the name). `portId` is Vanguard's price-series id (market data
//     `vanguard:nav:<portId>`); known funds get it from
//     `KNOWN_VANGUARD_PORT_IDS`, others ask once.
//   • fund-txn#<uuid>   — one transaction: `{ productId, date, kind: 'trade' |
//     'drp', units (signed decimal: + bought, − withdrawn), price (per unit,
//     AUD decimal money value), value (AUD money value, minor units, always
//     ≥ 0), importKey }`. A DRP (distribution reinvestment) buys units with a
//     distribution rather than new money.
//
// Pure derivations:
//   • `fundTotals` — units held, money put in / taken out, reinvested per fund.
//   • `fundParcels` + `allocateWithdrawals` — every purchase / reinvestment is
//     a CGT parcel (cost = its value); withdrawals use parcels oldest-first;
//     each lot carries its cost, proceeds (units × sale price), gain and the
//     12-month discount flag. Cost bases are ESTIMATES: the yearly AMIT cost
//     base adjustments Vanguard applies aren't modelled (their annual tax
//     statement has the exact figures).

import { financeStore } from './store.js';
import { cgtDiscountEligible, isoDate } from './fy.js';
import { allocateMinor, majorToMinor, money, priceTotal, readMoney } from './money.js';

export const FUND_PREFIX = 'fund#';
export const FUND_TXN_PREFIX = 'fund-txn#';
const CURRENCY = 'AUD';
/** Units are stored to this many decimal places (exports use 2–4). */
const UNIT_DECIMALS = 6;
const UNIT_EPSILON = 1e-6;

/**
 * Vanguard Australia managed funds: APIR code → price-series `portId` (from
 * Vanguard's public fund-detail API, which maps portId → APIR but has no
 * reverse lookup). Saves typing the id for the common funds.
 */
export const KNOWN_VANGUARD_PORT_IDS = Object.freeze({
  VAN0001AU: '8103',
  VAN0002AU: '8100',
  VAN0003AU: '8116',
  VAN0004AU: '8105',
  VAN0005AU: '8109',
  VAN0018AU: '8115',
  VAN0019AU: '8114',
  VAN0020AU: '8108',
  VAN0021AU: '8118',
  VAN0022AU: '8117',
  VAN0023AU: '8110',
  VAN0024AU: '8111',
  VAN0025AU: '8104',
  VAN0042AU: '8138',
  VAN0064AU: '8137',
  VAN0065AU: '8139',
  VAN0068AU: '8154',
  VAN0074AU: '8157',
  VAN0102AU: '8107',
  VAN0103AU: '8113',
  VAN0104AU: '8106',
  VAN0105AU: '8101',
  VAN0106AU: '8112',
  VAN0108AU: '8121',
  VAN0109AU: '8132',
  VAN0110AU: '8133',
  VAN0111AU: '8134',
  VAN0722AU: '8140',
  VAN0848AU: '8160',
  VAN1233AU: '8135',
  VAN1579AU: '8122',
  VAN2989AU: '8136',
  VAN3200AU: '8124',
  VAN3932AU: '8162',
  VAN4013AU: '8161',
  VAN4509AU: '8163',
  VAN8175AU: '8159',
  VAN8287AU: '8125',
  VAN9309AU: '8130',
});

function randomUuid() {
  return crypto.randomUUID();
}

export function normaliseProductId(id) {
  return String(id ?? '')
    .trim()
    .toUpperCase();
}

function normalisePortId(id) {
  const s = String(id ?? '').trim();
  return /^\d+$/.test(s) ? s : null;
}

/** The built-in price-series id for a fund, or null. */
export function knownPortId(productId) {
  return KNOWN_VANGUARD_PORT_IDS[normaliseProductId(productId)] ?? null;
}

/** The NAV (net asset value per unit) series id for a fund's portId. */
export function navSeriesId(portId) {
  return `vanguard:nav:${portId}`;
}

export function roundUnits(n) {
  return Number(Number(n).toFixed(UNIT_DECIMALS));
}

// ─── Funds ───────────────────────────────────────────────────────────────────

/** A fund's stored shape. */
export function normaliseFund(f) {
  const productId = normaliseProductId(f?.productId);
  return {
    productId,
    name: String(f?.name ?? '').trim() || productId,
    portId: normalisePortId(f?.portId) ?? knownPortId(productId),
    // Optional shorter name for tight spots (charts, deposits); null = derived.
    shortName: String(f?.shortName ?? '').trim() || null,
  };
}

export const fundItemId = (productId) => `${FUND_PREFIX}${normaliseProductId(productId)}`;

/** Every fund, by name. */
export async function listFunds(doc, userId) {
  const rows = await financeStore.listItems(doc, userId, FUND_PREFIX);
  return rows
    .map((r) => ({ itemId: r.itemId, ...normaliseFund(r) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Create or update a fund (keyed by its product id). */
export async function saveFund(doc, userId, fund) {
  const stored = normaliseFund(fund);
  if (!stored.productId) throw new RangeError('A fund needs a product id');
  const itemId = fundItemId(stored.productId);
  await financeStore.updateItem(doc, userId, itemId, stored);
  return { itemId, ...stored };
}

// ─── Transactions: normalisation + storage ──────────────────────────────────

/** A transaction's stored shape. */
export function normaliseFundTxn(t) {
  let date;
  try {
    date = isoDate(t?.date);
  } catch {
    date = isoDate(new Date());
  }
  const units = roundUnits(Number(t?.units) || 0);
  const price = readMoney(t?.price, { price: true, min: 0, currencies: [CURRENCY] }) ?? {
    amount: 0,
    currency: CURRENCY,
  };
  const value =
    readMoney(t?.value, { min: 0, currencies: [CURRENCY] }) ??
    money(Math.abs(priceTotal(units, price) ?? 0), CURRENCY);
  const stored = {
    productId: normaliseProductId(t?.productId),
    date,
    kind: t?.kind === 'drp' ? 'drp' : 'trade',
    units,
    price,
    value,
  };
  return { ...stored, importKey: fundTxnKey(stored) };
}

/**
 * The duplicate-detection key: fund | date | kind | units | value. Exports
 * carry no transaction id, so this identifies a row across re-imports.
 */
export function fundTxnKey(t) {
  return [
    normaliseProductId(t.productId),
    t.date,
    t.kind,
    Number(t.units).toFixed(4),
    t.value?.amount ?? 0,
  ].join('|');
}

const withId = (row, normalised) => ({
  itemId: row.itemId,
  createdAt: row.createdAt,
  ...normalised,
});

/** Transactions in date order (purchases before withdrawals on a day). */
export function sortFundTxns(txns) {
  return [...txns].sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      b.units - a.units ||
      String(a.itemId ?? '').localeCompare(String(b.itemId ?? '')),
  );
}

export async function listFundTxns(doc, userId) {
  const rows = await financeStore.listItems(doc, userId, FUND_TXN_PREFIX);
  return sortFundTxns(rows.map((r) => withId(r, normaliseFundTxn(r))));
}

export async function createFundTxn(doc, userId, txn, genId = randomUuid) {
  const stored = normaliseFundTxn(txn);
  const itemId = `${FUND_TXN_PREFIX}${genId()}`;
  const row = await financeStore.addItem(doc, userId, FUND_TXN_PREFIX, stored, itemId);
  return withId(row, stored);
}

/** Create many transactions (an import), in order. */
export async function createFundTxns(doc, userId, txns, genId = randomUuid) {
  const out = [];
  for (const t of txns) out.push(await createFundTxn(doc, userId, t, genId));
  return out;
}

export async function updateFundTxn(doc, userId, itemId, txn) {
  const stored = normaliseFundTxn(txn);
  await financeStore.updateItem(doc, userId, itemId, stored);
  return { itemId, ...stored };
}

export function deleteFundTxn(doc, userId, itemId) {
  return financeStore.deleteItem(doc, userId, itemId);
}

// ─── CSV import (Vanguard transaction export) ────────────────────────────────

/** The import schema for a Vanguard "investment transactions" export (lib/csv.js). */
export const VANGUARD_CSV_FIELDS = Object.freeze([
  { key: 'productId', label: 'Product ID', required: true, aliases: ['APIR', 'APIR code'] },
  { key: 'name', label: 'Investment', aliases: ['Fund', 'Fund name'] },
  { key: 'date', label: 'Trade Date', type: 'date', required: true, aliases: ['Date'] },
  { key: 'type', label: 'Type', required: true, aliases: ['Transaction type'] },
  { key: 'price', label: 'Unit Price', type: 'number', required: true, aliases: ['Price'] },
  { key: 'units', label: 'Quantity', type: 'number', required: true, aliases: ['Units'] },
  { key: 'value', label: 'Value', type: 'number', aliases: ['Amount'] },
]);

/** A transaction type label → kind: distribution reinvestments are 'drp'. */
export function fundTxnKind(type) {
  return /\bdrp\b|reinvest|distribution/i.test(String(type ?? '')) ? 'drp' : 'trade';
}

/**
 * One mapped CSV row (`mapCsvRows` values) → `{ txn, name }` or `{ error }`.
 * The value defaults to |units| × price when the column is absent.
 */
export function fundTxnFromCsv(values) {
  const productId = normaliseProductId(values.productId);
  if (!productId) return { error: 'Product ID is missing' };
  if (!values.units) return { error: 'Quantity is zero' };
  if (!(values.price > 0)) return { error: 'Unit Price must be more than zero' };
  const price = money(values.price, CURRENCY);
  const value =
    values.value === null || values.value === undefined
      ? Math.abs(priceTotal(values.units, price))
      : majorToMinor(Math.abs(values.value), CURRENCY);
  const txn = normaliseFundTxn({
    productId,
    date: values.date,
    kind: fundTxnKind(values.type),
    units: values.units,
    price,
    value: money(value, CURRENCY),
  });
  return { txn, name: values.name ?? productId };
}

// ─── Derived: totals ─────────────────────────────────────────────────────────

/**
 * Per fund (Map productId →) `{ productId, units, depositedCents,
 * withdrawnCents, reinvestedCents, netDepositedCents, firstDate, lastDate,
 * count }`, from transactions on/before `asOf` (default: all).
 */
export function fundTotals(txns, { asOf = null } = {}) {
  const totals = new Map();
  for (const t of sortFundTxns(txns)) {
    if (asOf && t.date > asOf) continue;
    let f = totals.get(t.productId);
    if (!f) {
      f = {
        productId: t.productId,
        units: 0,
        depositedCents: 0,
        withdrawnCents: 0,
        reinvestedCents: 0,
        netDepositedCents: 0,
        firstDate: t.date,
        lastDate: t.date,
        count: 0,
      };
      totals.set(t.productId, f);
    }
    f.units = roundUnits(f.units + t.units);
    const v = t.value.amount;
    if (t.kind === 'drp') f.reinvestedCents += v;
    else if (t.units > 0) f.depositedCents += v;
    else f.withdrawnCents += v;
    f.netDepositedCents = f.depositedCents - f.withdrawnCents;
    f.lastDate = t.date;
    f.count += 1;
  }
  return totals;
}

/** Units of one fund held at the end of `date`. */
export function unitsHeld(txns, productId, date) {
  let units = 0;
  for (const t of txns) {
    if (t.productId === productId && t.date <= date) units += t.units;
  }
  return roundUnits(units);
}

// ─── Derived: CGT parcels + withdrawals ──────────────────────────────────────

/** Every purchase / reinvestment as a parcel, oldest first. */
export function fundParcels(txns) {
  return sortFundTxns(txns)
    .filter((t) => t.units > 0)
    .map((t) => ({
      parcelRef: t.itemId ?? t.importKey,
      productId: t.productId,
      date: t.date,
      kind: t.kind,
      units: t.units,
      costCents: t.value.amount,
    }));
}

/**
 * Allocate each withdrawal (date order) to the same fund's parcels acquired
 * on/before it, oldest first. Cost moves with the units pro rata (a parcel's
 * last units take its remaining cost, so costs sum exactly). Each lot's
 * proceeds are its units × the sale price, to the cent (as Vanguard does).
 * @returns {{ withdrawals: Array<object>, remaining: Map<string, {units, costCents}> }}
 *   each withdrawal `{ ...txn, lots, unallocatedUnits, proceedsCents,
 *   costCents, gainCents }`; each lot `{ parcelRef, acquired, kind, units,
 *   costCents, proceedsCents, gainCents, heldOverYear, discountEligible }`.
 */
export function allocateWithdrawals(txns) {
  const parcels = fundParcels(txns);
  const remaining = new Map(
    parcels.map((p) => [p.parcelRef, { units: p.units, costCents: p.costCents }]),
  );
  const withdrawals = sortFundTxns(txns)
    .filter((t) => t.units < 0)
    .map((w) => allocateWithdrawal(w, parcels, remaining));
  return { withdrawals, remaining };
}

/** One withdrawal's lots, drawn oldest-first from `remaining` (mutated). */
function allocateWithdrawal(w, parcels, remaining) {
  let left = -w.units;
  const picks = [];
  for (const p of parcels) {
    if (left <= UNIT_EPSILON) break;
    if (p.productId !== w.productId || p.date > w.date) continue;
    const r = remaining.get(p.parcelRef);
    if (r.units <= UNIT_EPSILON) continue;
    const units = roundUnits(Math.min(left, r.units));
    const exhausts = r.units - units <= UNIT_EPSILON;
    const costCents = exhausts ? r.costCents : Math.round((r.costCents * units) / r.units);
    r.units = exhausts ? 0 : roundUnits(r.units - units);
    r.costCents -= costCents;
    left = roundUnits(left - units);
    picks.push({ p, units, costCents });
  }
  const unallocatedUnits = left > UNIT_EPSILON ? left : 0;
  // Lots' proceeds = units × the sale price, to the cent (as the fund's tax
  // report does — they may sum a cent off the withdrawal's value). With no
  // price, split the value by units (units with no parcel — missing history —
  // keep their share).
  const proceeds =
    w.price.amount > 0
      ? picks.map((k) => priceTotal(k.units, w.price))
      : picks.length
        ? allocateMinor(w.value.amount, [...picks.map((k) => k.units), unallocatedUnits])
        : [];
  const lots = picks.map(({ p, units, costCents }, i) => {
    const gainCents = proceeds[i] - costCents;
    const heldOverYear = cgtDiscountEligible(p.date, w.date);
    return {
      parcelRef: p.parcelRef,
      acquired: p.date,
      kind: p.kind,
      units,
      costCents,
      proceedsCents: proceeds[i],
      gainCents,
      heldOverYear,
      discountEligible: heldOverYear && gainCents > 0,
    };
  });
  const sum = (key) => lots.reduce((s, l) => s + l[key], 0);
  return {
    ...w,
    lots,
    unallocatedUnits,
    proceedsCents: sum('proceedsCents'),
    costCents: sum('costCents'),
    gainCents: sum('gainCents'),
  };
}
